import { Avatar, Typography, Box, Stack } from '@mui/joy';
import VerifiedBadge, { verifiedBadgeText } from '../../ui/VerifiedBadge';
import Badge, { badgeClasses } from '@mui/joy/Badge';
import { useTheme } from '@mui/joy/styles';
import { Fancybox } from "@fancyapps/ui"
import "@fancyapps/ui/dist/fancybox/fancybox.css"
import { useCallback } from 'react';
import { useIsOnline } from '../../app/OnlineProvider';
import { AchievementOrbit } from './AchievementOrbit';
import { mediaUrl } from '../../../utils/mediaUrl';

/** Аватар в шапке с шапкой-обложкой и без неё. */
const AVATAR_SIZE_CAP = 124;
const AVATAR_SIZE_NO_CAP = 96;

/**
 * Потолок ширины шапки без обложки.
 *
 * На телефоне не влияет ни на что — шапка как была во всю ширину, так и
 * остаётся. На широком экране упирается в потолок и держится по центру: иначе
 * аватар прижат к левому краю, текст — к правому, а между ними полоса пустоты.
 */
const CONTENT_MAX_WIDTH = 500;

function ShowAvatar({ avatar = null, openImage, isOnline = false, size = AVATAR_SIZE_CAP }) {
    const theme = useTheme();

    const sxAvatar = {
        width: size,
        height: size,
        border: '4px solid',
        borderColor: theme.palette.background.body,
        cursor: avatar ? 'pointer' : 'default',
        pointerEvents: 'all'
    };

    // Точка «в сети» соразмерна аватару: на уменьшенном она бы выглядела кляксой.
    const dotSize = size >= AVATAR_SIZE_CAP ? 18 : 16;

    return (
        <Badge
            invisible={!isOnline}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeInset="14%"
            color="success"
            sx={{
                [`& .${badgeClasses.badge}`]: {
                    width: dotSize,
                    height: dotSize,
                    borderRadius: '50%',
                    '&::after': {
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        border: '3px solid',
                        borderColor: theme.palette.background.body,
                        boxSizing: 'content-box',
                        transform: 'translate(-3px, -3px) !important',
                        content: '""',
                    },
                },
            }}
        >
            <Avatar
                src={mediaUrl(avatar)}
                sx={sxAvatar}
                onClick={() => {
                    const url = mediaUrl(avatar);
                    if (url) openImage(url);
                }}
            />
        </Badge>
    );
}

/**
 * Имя, подпись о верификации, @username и описание.
 *
 * Один и тот же текст в обеих раскладках шапки — отличается только
 * выравниванием, поэтому оно и вынесено в параметр.
 *
 * @param {'center'|'start'} align
 */
function ProfileTexts({ firstName, lastName, verified = false, username = null, description = null, align = 'center' }) {
    const verifiedText = verified ? verifiedBadgeText(verified) : null;

    const sxText = {
        maxWidth: '450px',
        width: '100%',
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap',
        textAlign: align,
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: align === 'center' ? 'center' : 'flex-start',
                minWidth: 0,
                ...(align && { width: 'fit-content' }),
                ...(align && { width: '100%' }),
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: align === 'center' ? 'center' : 'flex-start',
                    gap: '6px',
                    maxWidth: '100%',
                    ...(align && { width: 'fit-content' }),
                    ...(align && { width: '100%' }),
                }}
            >
                <Typography
                    level="title-md"
                    sx={{
                        textAlign: align,
                        fontWeight: 600,
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                    }}
                >
                    {firstName ?? ""} {lastName ?? ""}
                </Typography>
                {!!verified && <VerifiedBadge size={18} type={verified} />}
            </Box>

            {!!verifiedText && (
                <Typography
                    level="body-sm"
                    textColor="primary.500"
                    sx={{ ...sxText, mt: '.125rem', fontWeight: 600 }}
                >
                    {verifiedText}
                </Typography>
            )}

            {Boolean(username) && (
                <Typography
                    level="body-sm"
                    textColor="text.tertiary"
                    sx={{ ...sxText, my: '.125rem', mb: '.2rem', fontWeight: 600 }}
                >
                    {`@${username}`}
                </Typography>
            )}

            {!!description && (
                <Typography level="body-sm" textColor="text.tertiary" sx={sxText}>
                    {description ?? ""}
                </Typography>
            )}
        </Box>
    );
}

function ProfileAvatar({ avatar, cap, firstName, lastName, verified = false, description, userId = null, username = null, achievements = [] }) {
    const isOnline = useIsOnline(userId);
    const capUrl = () => mediaUrl(cap);

    const openImage = useCallback((src) => {
        Fancybox.show(
            [
                {
                    src,
                    type: "image",
                },
            ],
            {
                animated: false,
                dragToClose: true,
                closeButton: "top",
                Thumbs: false,
                Toolbar: {
                    display: ["close", "zoom"],
                },
                Carousel: {
                    infinite: false,
                },
            }
        );
    }, []);

    const texts = (align) => (
        <ProfileTexts
            firstName={firstName}
            lastName={lastName}
            verified={verified}
            username={username}
            description={description}
            align={align}
        />
    );

    // Без обложки прятать нечего под аватаром: шапка становится строкой
    // «слева значок — справа текст», а по центру держится блок целиком.
    if (!cap) {
        return (
            <Box sx={{ width: '100%', mb: 4 }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    sx={{
                        mt: 2,
                        px: 2,
                        gap: 4,
                        width: '100%',
                        maxWidth: `${CONTENT_MAX_WIDTH}px`,
                        mx: 'auto',
                        pointerEvents: 'none',
                        justifyContent: 'center'
                    }}
                >
                    <AchievementOrbit achievements={achievements} avatarSize={AVATAR_SIZE_NO_CAP}>
                        <ShowAvatar
                            avatar={avatar ?? null}
                            openImage={openImage}
                            isOnline={isOnline}
                            size={AVATAR_SIZE_NO_CAP}
                        />
                    </AchievementOrbit>

                    {/* Колонка занимает остаток строки: без этого длинное имя
                        раздвинуло бы её за края экрана вместо переноса. */}
                    <Box>
                        {texts('start')}
                    </Box>
                </Stack>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', mb: 4 }}>
            <Box
                onClick={() => openImage(capUrl())}
                sx={{
                    height: 220,
                    backgroundImage: `url(${capUrl()})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center center',
                    borderRadius: '24px',
                    cursor: 'pointer'
                }}
            />
            <Stack
                direction="column"
                alignItems="center"
                sx={{
                    mt: '-62px',
                    pointerEvents: 'none',
                    px: 2
                }}
            >
                <AchievementOrbit achievements={achievements} avatarSize={AVATAR_SIZE_CAP}>
                    <ShowAvatar
                        avatar={avatar ?? null}
                        openImage={openImage}
                        isOnline={isOnline}
                        size={AVATAR_SIZE_CAP}
                    />
                </AchievementOrbit>

                <Box sx={{ mt: 1, width: '100%' }}>
                    {texts('center')}
                </Box>
            </Stack>
        </Box>
    )
}

export default ProfileAvatar
